import type { KeyPair } from "@foreman/crypto";
import { CloseCode, isPairingExpired, tokenToString, type PairingPayload } from "@foreman/protocol";
import { InitiatorLink } from "./links";
import type { Connector } from "./remote-client";

export type PairingFailure = "expired" | "rejected" | "offline" | "timeout" | "connection" | "protocol";

export class PairingError extends Error {
  constructor(
    readonly code: PairingFailure,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Phone side of pairing: connect to the computer named in the code, present the one-time
 * token over the end-to-end encrypted channel, and report whether the computer accepted this
 * device. The phone's private key never leaves it; only its public key is revealed, and only
 * to the computer (the relay sees ciphertext). On success, keep `payload.daemonPublicKey` and
 * `payload.relayUrl` and connect normally with a RemoteClient.
 */
export function pairWithDaemon(opts: {
  payload: PairingPayload;
  keys: KeyPair;
  connect: Connector;
  /** Label shown in the computer's device list. */
  deviceName: string;
  /** How long to wait for the socket to the relay to open before giving up. Default 30s. */
  connectTimeoutMs?: number;
  timeoutMs?: number;
  /** How long to wait for the person at the computer to allow this phone once the code was accepted. Default 2 minutes. */
  approvalTimeoutMs?: number;
  /** Called when the computer is asking its owner to allow this phone. */
  onPending?: () => void;
  now?: () => number;
  /** Abort a pairing in flight (the user pressed Cancel). */
  signal?: AbortSignal;
}): Promise<{ deviceId: string }> {
  const { payload, keys } = opts;
  if (isPairingExpired(payload, opts.now?.())) {
    return Promise.reject(new PairingError("expired", "this pairing code has expired; generate a new one on the computer"));
  }
  return new Promise((resolve, reject) => {
    let settled = false;
    let transport: ReturnType<Connector> | null = null;
    const finish = (err: PairingError | null, deviceId?: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onAbort);
      transport?.close(1000, "pairing finished");
      if (err) reject(err);
      else resolve({ deviceId: deviceId! });
    };
    // Two separate clocks: opening the socket to the relay can be slow on its own (a slow or
    // inspected network can take tens of seconds just to establish TLS), and that dial-up time
    // must not eat into the budget for the actual phone<->computer handshake once the pipe is up.
    let timer = setTimeout(() => finish(new PairingError("timeout", "could not reach the relay in time")), opts.connectTimeoutMs ?? 30_000);
    const onAbort = () => finish(new PairingError("connection", "pairing was cancelled"));
    if (opts.signal?.aborted) return onAbort();
    opts.signal?.addEventListener("abort", onAbort, { once: true });

    const link = new InitiatorLink({
      keys,
      daemonPublicKey: payload.daemonPublicKey,
      sendRaw: (f) => transport?.send(f),
      onReady: () => link.send({ t: "pair.request", token: tokenToString(payload.token), name: fitDeviceName(opts.deviceName) }),
      onMessage: (m) => {
        if (m.t === "pair.accepted") finish(null, m.deviceId);
        else if (m.t === "pair.pending") {
          // the code was right; a person has to say yes, which can take longer than a network round trip
          clearTimeout(timer);
          timer = setTimeout(() => finish(new PairingError("timeout", "nobody allowed this phone on the computer in time")), opts.approvalTimeoutMs ?? 120_000);
          opts.onPending?.();
        } else if (m.t === "pair.rejected") finish(new PairingError("rejected", "the computer did not accept this phone (wrong, expired or used code, or it was not allowed)"));
        else finish(new PairingError("protocol", `unexpected reply: ${m.t}`));
      },
    });
    transport = opts.connect({
      onOpen: () => {
        // The socket is up: now start the strict clock for the computer's actual reply.
        clearTimeout(timer);
        timer = setTimeout(() => finish(new PairingError("timeout", "the computer did not answer in time")), opts.timeoutMs ?? 15_000);
        link.start();
      },
      onMessage: (data) => {
        try {
          link.receive(data);
        } catch (e) {
          finish(new PairingError("protocol", (e as Error).message));
        }
      },
      onClose: (code) => {
        if (code === CloseCode.DaemonOffline) finish(new PairingError("offline", "that computer is not online (is `foreman daemon` running?)"));
        else if (code === CloseCode.DeviceRejected) finish(new PairingError("rejected", "the computer is not accepting new devices right now"));
        else finish(new PairingError("connection", `connection closed before pairing finished (${code})`));
      },
    });
  });
}

/** The longest device name the computer accepts (see `pair.request` in the protocol). */
export const MAX_DEVICE_NAME = 64;

/**
 * A name the computer will accept: trimmed, at most 64 characters (a long hostname must not stop pairing), never empty.
 * Cuts on whole characters, so an emoji is never split in half.
 */
export function fitDeviceName(name: string): string {
  const t = name.trim() || "Device";
  if (t.length <= MAX_DEVICE_NAME) return t;
  let out = "";
  for (const ch of t.slice(0, MAX_DEVICE_NAME - 1)) out += ch;
  if (/[\ud800-\udbff]$/.test(out)) out = out.slice(0, -1); // a surrogate pair cut in two
  return `${out.trimEnd()}…`;
}
